import { isTotalRow } from './parsers';
import type { DetectedStructure } from './parsers/types';
import type { PdfExtractionSummary } from './parsers/pdfLoader';
import type { WizardAction } from './wizardReducer';

type ParsePayload = Extract<WizardAction, { type: 'PARSE_SUCCEEDED' }>['payload'];

/**
 * Rows are keyed by header text, so two columns with the same header (e.g.
 * "Customer,Class,Class") used to collide — the last column silently replaced
 * the first in every row (ENG-499). Repeats get a " (2)", " (3)" suffix; the
 * first occurrence keeps its name, so files without duplicates are unchanged.
 */
export function uniqueHeaders(header: string[]): string[] {
	const seen = new Map<string, number>();
	return header.map((h) => {
		const count = (seen.get(h) ?? 0) + 1;
		seen.set(h, count);
		return count === 1 ? h : `${h} (${count})`;
	});
}

/**
 * Build the PARSE_SUCCEEDED payload from a DetectedStructure. Shared by the Step 1
 * initial parse (Step1Upload) and the Step 2 sheet switch (Step2MapColumns) so both
 * derive the header-keyed `rawRows` identically — ENG-383 introduced the second
 * call site, and this keeps the two from drifting.
 *
 * `extraction` is only ever passed for a PDF upload (Step1Upload's PDF branch);
 * every other call site omits it, explicitly clearing pdfExtraction to null —
 * see wizardReducer.ts's comment on why that field is required, not optional.
 */
export function buildParsePayload(
	detected: DetectedStructure,
	extraction: PdfExtractionSummary | null = null
): ParsePayload {
	const header = uniqueHeaders(
		(detected.rawRows[detected.headerRowIndex] ?? []).map((h) => String(h))
	);
	// Every consumer reads headers back from rawMatrix[headerRowIndex], so it
	// must carry the same de-duplicated names the rows are keyed by.
	const rawMatrix = detected.rawRows.map((r, i) =>
		i === detected.headerRowIndex ? header : r
	);
	const dataRows = detected.rawRows
		.slice(detected.headerRowIndex + 1)
		.filter((r) => r.some((c) => String(c).trim() !== '') && !isTotalRow(r));
	const keyed = dataRows.map((r) => {
		const o: Record<string, unknown> = {};
		header.forEach((h, i) => {
			o[h] = r[i] ?? '';
		});
		return o;
	});

	return {
		sheets: detected.sheets,
		selectedSheet: detected.selectedSheet,
		headerRowIndex: detected.headerRowIndex,
		rawMatrix,
		rawRows: keyed,
		pdfExtraction: extraction,
	};
}
